import React, { useEffect, useState } from "react";
import { Button, Grid, H2, Icon, SystemMessage, Text, TextField } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MetaFBPageImage1 from "../../assets/meta-fb-page-1.png";
import WherePageIdVideoCZ from "../../assets/video/find_fb_page_id.cz.mp4";
import WherePageIdVideoSK from "../../assets/video/find_fb_page_id.sk.mp4";
import HowToControlConnectionVideoCZ from "../../assets/video/how_to_control_fb_connection.cz.mp4";
import HowToControlConnectionVideoSK from "../../assets/video/how_to_control_fb_connection.sk.mp4";
import HowToCreateAPageVideoCZ from "../../assets/video/how_to_create_fb_page.cz.mp4";
import HowToCreateAPageVideoSK from "../../assets/video/how_to_create_fb_page.sk.mp4";
import { WIZARD_STEP_PATH } from "../../constants/paths";
import MetaWizardFooter from "../../elements/MetaWizard/MetaWizardFooter";
import MetaWizardHeader from "../../elements/MetaWizard/MetaWizardHeader";
import { ModalWrapper } from "../../elements/ModalWrapper";
import WizardHeader from "../../elements/WizardHeader";
import {
  AdSystemT,
  WizardStepT,
  useChangeMetaAdsSettingsMutation,
  useCheckMetaAdsClaimPageApprovalMutation,
  useFacebookPageInfoQuery,
} from "../../graphql/generated/graphql";
import { useSetWizardStepEffect } from "./useSetWizardStepEffect";

type PageState = "INITIAL" | "ENTER_PAGE_ID" | "DONT_HAVE_A_PAGE" | "WAITING_CONFIRMATION" | "CAPACITY_LIMIT";

export const MetaFacebookPage = () => {
  const { i18n, t } = useTranslation("translation", { keyPrefix: "Wizard Facebook Page" });
  const isSK = i18n.language === "sk";
  const WherePageIdVideo = isSK ? WherePageIdVideoSK : WherePageIdVideoCZ;
  const HowToControlConnectionVideo = isSK ? HowToControlConnectionVideoSK : HowToControlConnectionVideoCZ;
  const HowToCreateAPageVideo = isSK ? HowToCreateAPageVideoSK : HowToCreateAPageVideoCZ;
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>("INITIAL");
  const [facebookPageId, setFacebookPageId] = useState<string>("");
  const [isFacebookIdModalOpen, setIsFacebookIdModalOpen] = useState<boolean>(false);
  const [isWhatPageShouldContainModalOpen, setIsWhatPageShouldContainModalOpen] = useState<boolean>(false);
  const [isHowToConfirmModalOpen, setIsHowToConfirmModalOpen] = useState<boolean>(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState<boolean>(false);

  const { data, refetch } = useFacebookPageInfoQuery();
  const [changeMetaAdsSettings] = useChangeMetaAdsSettingsMutation();
  const [submittingPageId, setSubmittingPageId] = useState(false);
  const [checkMetaAdsClaimPageApproval, { loading }] = useCheckMetaAdsClaimPageApprovalMutation();
  const [showNotConfirmedMessage, setShowNotConfirmedMessage] = useState<boolean>(false);
  const [changeSettingsError, setChangeSettingsError] = useState<string | null>(null);
  const [isRefetchingPageInfo, setIsRefetchingPageInfo] = useState<boolean>(false);

  useEffect(() => {
    const setting = data?.organization?.metaAdsSetting;
    if (!setting) {
      return;
    }

    if (setting.pageId) {
      setFacebookPageId(setting.pageId);
    }

    if (setting.pageClaimApproved) {
      navigate(WIZARD_STEP_PATH.META_PRODUCTS_SELECTION);
    } else if (setting.pageClaimFailureReason === "invalid_page_id") {
      setPageState("ENTER_PAGE_ID");
    } else if (setting.pageClaimFailureReason === "capacity") {
      setPageState("CAPACITY_LIMIT");
    } else if (setting.pageClaimRequestedAt) {
      setPageState("WAITING_CONFIRMATION");
    } else if (setting.pageId) {
      setPageState("ENTER_PAGE_ID");
    }
  }, [data, navigate]);

  const dontHavePageModalOpenHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsWhatPageShouldContainModalOpen(true);
  };

  const openFacebookIdModal = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsFacebookIdModalOpen(true);
  };

  const closeFacebookIdModal = () => {
    setIsFacebookIdModalOpen(false);
  };

  const closeWhatPageShouldContainModal = () => {
    setIsWhatPageShouldContainModalOpen(false);
  };

  const backButtonHandler = () => {
    if (pageState === "INITIAL") {
      navigate(WIZARD_STEP_PATH.META_FIRST);
    } else if (pageState === "ENTER_PAGE_ID" || pageState === "DONT_HAVE_A_PAGE") {
      setPageState("INITIAL");
    }
  };

  const nextButtonHandler = async () => {
    setSubmittingPageId(true);
    try {
      const result = await changeMetaAdsSettings({
        variables: {
          pageId: facebookPageId,
        },
      });

      const errors = result.data?.changeMetaAdsSettings?.errors;
      if (errors && errors.length > 0) {
        setChangeSettingsError(t("Submit error - page id locked"));
        return;
      }

      setChangeSettingsError(null);
      await refetch();
      setPageState("WAITING_CONFIRMATION");
    } finally {
      setSubmittingPageId(false);
    }
  };

  const handleCheckClaimPageApproval = async () => {
    try {
      setShowNotConfirmedMessage(false);
      await checkMetaAdsClaimPageApproval();
      const refetchResult = await refetch();

      if (refetchResult.data?.organization?.metaAdsSetting?.pageClaimApproved === false) {
        setShowNotConfirmedMessage(true);
      }
    } catch (err) {
      throw err;
    }
  };

  useSetWizardStepEffect({ adSystem: AdSystemT.MetaAdsT, wizardStep: WizardStepT.MetaPageLinkingT });
  return (
    <div>
      <WizardHeader />
      <MetaWizardHeader activeStep={2} />
      {pageState === "INITIAL" && (
        <Grid columns={2}>
          <div>
            <H2 divider={false}>{t("Heading - initial")}</H2>
            <Text>
              <Trans components={{ b: <b /> }} i18nKey="Description - initial" t={t} />
            </Text>
            <div style={{ margin: "20px 0" }}>
              <SystemMessage description={<Text>{t("System info - initial")}</Text>} level="notice" />
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                iconLeft="shp-cross"
                size="md"
                variant="secondary"
                onClick={() => setPageState("DONT_HAVE_A_PAGE")}
              >
                {t("Dont have page cta")}
              </Button>
              <Button
                iconLeft="shp-checkmark"
                size="md"
                variant="secondary"
                onClick={() => setPageState("ENTER_PAGE_ID")}
              >
                {t("Have page cta")}
              </Button>
            </div>
          </div>
          <div>
            <img alt="" src={MetaFBPageImage1} style={{ borderRadius: "var(--app-radius-box)" }} />
          </div>
        </Grid>
      )}
      {pageState === "DONT_HAVE_A_PAGE" && (
        <Grid columns={2}>
          <div>
            <H2 divider={false}>{t("Heading - dont have page")}</H2>
            <div style={{ marginBottom: "20px" }}>
              <Trans
                components={{ a: <a href="#" onClick={dontHavePageModalOpenHandler} />, b: <b /> }}
                i18nKey="Description - dont have page - 1"
                t={t}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <Trans components={{ b: <b /> }} i18nKey="Description - dont have page - 2" t={t} />
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                iconRight="shp-external-link"
                size="md"
                variant="secondary"
                onClick={() => window.open("https://www.facebook.com/pages/create", "_blank")}
              >
                {t("Go to Facebook cta")}
              </Button>
              <Button size="md" variant="primary" onClick={() => setPageState("ENTER_PAGE_ID")}>
                {t("I have created page cta")}
              </Button>
            </div>
          </div>
          <div>
            <video
              src={HowToCreateAPageVideo}
              style={{ width: "100%", borderRadius: "var(--app-radius-box)" }}
              controls
            />
          </div>
        </Grid>
      )}
      {pageState === "ENTER_PAGE_ID" && (
        <Grid columns={2}>
          <div>
            <H2 divider={false}>{t("Heading - enter page id")}</H2>
            <div style={{ marginBottom: "20px" }}>
              <Text>{t("Description - enter page id")}</Text>
            </div>
            {data?.organization?.metaAdsSetting?.pageClaimRequestedAt &&
              !data?.organization?.metaAdsSetting?.pageClaimApproved &&
              data?.organization?.metaAdsSetting?.pageClaimFailureReason !== "invalid_page_id" && (
                <div style={{ marginBottom: "20px" }}>
                  <SystemMessage
                    description={<Text>{t("Waiting confirmation - enter page id banner")}</Text>}
                    level="alert"
                  />
                </div>
              )}
            {data?.organization?.metaAdsSetting?.pageClaimFailureReason === "invalid_page_id" && (
              <div style={{ marginBottom: "20px" }}>
                <SystemMessage description={<Text>{t("Invalid page id - banner")}</Text>} level="alert" />
              </div>
            )}
            {data?.organization?.metaAdsSetting?.pageClaimFailureReason === "retries_exhausted" && (
              <div style={{ marginBottom: "20px" }}>
                <SystemMessage description={<Text>{t("Expired request - banner")}</Text>} level="alert" />
              </div>
            )}
            {changeSettingsError && (
              <div style={{ marginBottom: "20px" }}>
                <SystemMessage description={<Text>{changeSettingsError}</Text>} level="alert" />
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                margin: "20px 0",
                padding: "24px 0",
                background: "var(--app-surface-muted)",
                borderRadius: "var(--app-radius-box)",
              }}
            >
              <TextField
                hint={t("facebook id input hint")}
                inputMode="numeric"
                label={t("facebook id input label")}
                name="facebook_page_id"
                value={facebookPageId}
                disabled={
                  (!!data?.organization?.metaAdsSetting?.pageClaimRequestedAt &&
                    !data?.organization?.metaAdsSetting?.pageClaimApproved &&
                    data?.organization?.metaAdsSetting?.pageClaimFailureReason !== "invalid_page_id") ||
                  data?.organization?.metaAdsSetting?.pageClaimFailureReason === "capacity"
                }
                labelAfter={
                  <Text>
                    <a href="#" onClick={openFacebookIdModal}>
                      {t("facebook id input tooltip label")}
                    </a>
                  </Text>
                }
                onChange={(newValue) => setFacebookPageId(newValue.replace(/\D/g, ""))}
              />
            </div>
          </div>
          <div>
            <video src={WherePageIdVideo} style={{ width: "100%", borderRadius: "var(--app-radius-box)" }} controls />
          </div>
        </Grid>
      )}
      {pageState === "WAITING_CONFIRMATION" && (
        <Grid columns={2}>
          <div>
            <H2 divider={false}>{t("Heading - waiting confirmation")}</H2>
            <div style={{ marginBottom: "20px" }}>
              <Trans
                i18nKey="Description - waiting confirmation - 1"
                t={t}
                components={{
                  a: (
                    <a
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setIsHowToConfirmModalOpen(true);
                      }}
                    />
                  ),
                  b: <b />,
                }}
                values={{
                  userEmail: data?.user?.email,
                  facebookPageId: data?.organization?.metaAdsSetting?.pageId,
                }}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <Trans
                i18nKey="Description - waiting confirmation - 2"
                t={t}
                components={{
                  a: <a />,
                  faq: (
                    <a
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setIsFaqModalOpen(true);
                      }}
                    />
                  ),
                }}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <SystemMessage
                level="alert"
                description={
                  <Text>
                    <Trans components={{ a: <a /> }} i18nKey="Waiting confirmation - personalized ads warning" t={t} />
                  </Text>
                }
              />
            </div>
            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <Button size="md" variant="secondary" onClick={() => setPageState("ENTER_PAGE_ID")}>
                {t("Back - cta")}
              </Button>
              <Button
                iconLeft="shp-loop"
                isLoading={loading}
                size="md"
                variant="secondary"
                onClick={handleCheckClaimPageApproval}
              >
                {t("Waiting confirmation - cta")}
              </Button>
            </div>
            {showNotConfirmedMessage && (
              <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                <Icon icon="shp-info-small" />
                <div>
                  <Text>{t("Waiting confirmation - not confirmed yet")}</Text>
                </div>
              </div>
            )}
          </div>
          <div>
            <video
              src={HowToControlConnectionVideo}
              style={{ width: "100%", borderRadius: "var(--app-radius-box)" }}
              controls
            />
          </div>
        </Grid>
      )}
      {pageState === "CAPACITY_LIMIT" && (
        <div>
          <H2 divider={false}>{t("Capacity limit - heading")}</H2>
          <div style={{ marginBottom: "20px" }}>
            <SystemMessage
              level="alert"
              title={t("Capacity limit - banner title")}
              description={
                <Text>
                  <Trans components={{ a: <a />, b: <b /> }} i18nKey="Capacity limit - description" t={t} />
                </Text>
              }
            />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button size="md" variant="secondary" onClick={() => setPageState("ENTER_PAGE_ID")}>
              {t("Back - cta")}
            </Button>
            <Button
              iconLeft="shp-loop"
              isLoading={isRefetchingPageInfo}
              size="md"
              variant="secondary"
              onClick={async () => {
                setIsRefetchingPageInfo(true);
                try {
                  await refetch();
                } finally {
                  setIsRefetchingPageInfo(false);
                }
              }}
            >
              {t("Capacity limit - cta")}
            </Button>
          </div>
        </div>
      )}
      <MetaWizardFooter
        backButtonHandler={backButtonHandler}
        nextButtonDisabled={facebookPageId === "" || submittingPageId}
        nextButtonHandler={nextButtonHandler}
        showBackButton={pageState !== "WAITING_CONFIRMATION" && pageState !== "CAPACITY_LIMIT"}
        showNextButton={pageState === "ENTER_PAGE_ID"}
      />
      <ModalWrapper isOpen={isFacebookIdModalOpen} title={t("facebook id modal title")} onClose={closeFacebookIdModal}>
        <Text>{t("facebook id modal text")}</Text>
        <ol style={{ marginTop: 0, paddingLeft: "15px" }}>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="facebook id modal step 1" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="facebook id modal step 2" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="facebook id modal step 3" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="facebook id modal step 4" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="facebook id modal step 5" t={t} />
          </li>
        </ol>
      </ModalWrapper>
      <ModalWrapper
        isOpen={isHowToConfirmModalOpen}
        title={t("How to confirm modal title")}
        onClose={() => setIsHowToConfirmModalOpen(false)}
      >
        <ol style={{ marginTop: 0, paddingLeft: "15px" }}>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="How to confirm modal step 1" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="How to confirm modal step 2" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="How to confirm modal step 3" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="How to confirm modal step 4" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="How to confirm modal step 5" t={t} />
          </li>
        </ol>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="How to confirm modal business manager intro" t={t} />
        </Text>
        <ol style={{ marginTop: 0, paddingLeft: "15px" }}>
          <li>
            <Trans
              components={{ a: <a rel="noopener noreferrer" target="_blank" />, b: <b /> }}
              i18nKey="How to confirm modal business manager step 1"
              t={t}
            />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="How to confirm modal business manager step 2" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="How to confirm modal business manager step 3" t={t} />
          </li>
          <li>
            <Trans components={{ b: <b /> }} i18nKey="How to confirm modal business manager step 4" t={t} />
          </li>
        </ol>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="How to confirm modal note" t={t} />
        </Text>
      </ModalWrapper>
      <ModalWrapper
        isOpen={isFaqModalOpen}
        title={t("Faq modal title")}
        maxWidth
        onClose={() => setIsFaqModalOpen(false)}
      >
        <div style={{ marginBottom: "20px" }}>
          <Text>
            <b>{t("Faq modal 1 title")}</b>
          </Text>
          <div>
            <Trans components={{ b: <b /> }} i18nKey="Faq modal 1 text" t={t} />
          </div>
        </div>
        <div style={{ marginBottom: "20px" }}>
          <Text>
            <b>{t("Faq modal 2 title")}</b>
          </Text>
          <div>
            <Trans components={{ b: <b /> }} i18nKey="Faq modal 2 text" t={t} />
          </div>
        </div>
        <div style={{ marginBottom: "20px" }}>
          <Text>
            <b>{t("Faq modal 3 title")}</b>
          </Text>
          <div>
            <Trans components={{ b: <b /> }} i18nKey="Faq modal 3 text" t={t} />
          </div>
        </div>
        <div style={{ marginBottom: "20px" }}>
          <Text>
            <b>{t("Faq modal 4 title")}</b>
          </Text>
          <div>
            <Trans components={{ b: <b /> }} i18nKey="Faq modal 4 intro" t={t} />
          </div>
          <ul style={{ marginTop: 0, paddingLeft: "15px" }}>
            <li>
              <Trans
                components={{ a: <a rel="noopener noreferrer" target="_blank" />, b: <b /> }}
                i18nKey="Faq modal 4 step 1"
                t={t}
              />
            </li>
            <li>
              <Trans components={{ b: <b /> }} i18nKey="Faq modal 4 step 2" t={t} />
            </li>
            <li>
              <Trans components={{ b: <b /> }} i18nKey="Faq modal 4 step 3" t={t} />
            </li>
            <li>
              <Trans components={{ b: <b /> }} i18nKey="Faq modal 4 step 4" t={t} />
            </li>
          </ul>
        </div>
        <div>
          <Text>
            <b>{t("Faq modal 5 title")}</b>
          </Text>
          <div>
            <Trans components={{ b: <b /> }} i18nKey="Faq modal 5 text" t={t} />
          </div>
        </div>
      </ModalWrapper>
      <ModalWrapper
        isOpen={isWhatPageShouldContainModalOpen}
        title={t("what page should contain modal title")}
        onClose={closeWhatPageShouldContainModal}
      >
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="what page should contain modal content" t={t} />
        </Text>
        <br />
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="what page should contain modal list 1 title" t={t} />
        </Text>
        <ul style={{ margin: 0, paddingLeft: "15px" }}>
          <li>{t("what page should contain modal list 1 item 1")}</li>
          <li>{t("what page should contain modal list 1 item 2")}</li>
          <li>{t("what page should contain modal list 1 item 3")}</li>
        </ul>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="what page should contain modal list 2 title" t={t} />
        </Text>
        <ul style={{ margin: 0, paddingLeft: "15px" }}>
          <li>{t("what page should contain modal list 2 item 1")}</li>
          <li>{t("what page should contain modal list 2 item 2")}</li>
        </ul>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="what page should contain modal list 3 title" t={t} />
        </Text>
        <ul style={{ margin: 0, paddingLeft: "15px" }}>
          <li>{t("what page should contain modal list 1 item 1")}</li>
          <li>{t("what page should contain modal list 1 item 2")}</li>
        </ul>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="what page should contain modal list 4 title" t={t} />
        </Text>
        <ul style={{ margin: 0, paddingLeft: "15px" }}>
          <li>{t("what page should contain modal list 4 item 1")}</li>
        </ul>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="what page should contain modal list 5 title" t={t} />
        </Text>
        <ul style={{ margin: 0, paddingLeft: "15px" }}>
          <li>{t("what page should contain modal list 5 item 1")}</li>
          <li>{t("what page should contain modal list 5 item 2")}</li>
        </ul>
        <Text>
          <Trans components={{ b: <b /> }} i18nKey="what page should contain modal list 6 title" t={t} />
        </Text>
        <ul style={{ margin: 0, paddingLeft: "15px" }}>
          <li>{t("what page should contain modal list 6 item 1")}</li>
        </ul>
      </ModalWrapper>
    </div>
  );
};
